import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { Building2, Palette, Eye, Save, ImageIcon, Pencil } from 'lucide-react';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Spinner } from '@/shared/components/ui/Spinner';
import { ROUTES } from '@/routes/paths';
import { usePayrollSettings, useSavePayrollSettings } from './hooks/usePayroll';
import type { PayrollSettings } from './payrollTypes';
import { formatINR } from '@/shared/utils/currency';

function Toggle({ checked, onChange, label }: { checked: boolean; onChange: (v: boolean) => void; label: string }) {
  return (
    <label className="flex items-center justify-between cursor-pointer group">
      <span className="text-body-md text-on-surface group-hover:text-primary transition-colors">{label}</span>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary/40 ${checked ? 'bg-primary' : 'bg-outline'}`}
      >
        <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-card shadow transition-transform ${checked ? 'translate-x-4.5' : 'translate-x-0.5'}`} />
      </button>
    </label>
  );
}

export function PayrollSettingsTab() {
  const navigate = useNavigate();
  const { data: settings, isLoading } = usePayrollSettings();
  const saveSettings = useSavePayrollSettings();

  const [form, setForm] = useState<Partial<PayrollSettings>>({});
  const [showPreview, setShowPreview] = useState(false);

  // Merge saved settings with local form state
  const merged: Partial<PayrollSettings> = { ...settings, ...form };

  const set = (key: keyof PayrollSettings, val: unknown) =>
    setForm((prev: Partial<PayrollSettings>) => ({ ...prev, [key]: val }));

  const handleSave = async () => {
    try {
      await saveSettings.mutateAsync(form);
      setForm({});
      toast.success('Payroll settings saved!');
    } catch {
      toast.error('Failed to save settings');
    }
  };

  if (isLoading) return (
    <div className="p-10 flex justify-center"><Spinner /></div>
  );

  return (
    <div className="p-6 space-y-8 max-w-2xl">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="font-semibold text-on-surface flex items-center gap-2">
            <Building2 className="w-4 h-4 text-primary" /> Company Branding
          </h3>
          <p className="text-body-md text-on-surface-variant mt-1">
            Name, address, CIN, GST, and logo now come from Master Data &gt; Company Profile — the same record used for letterhead documents, so it only needs to be set once.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => navigate(ROUTES.companyProfileMaster)}>
          <Pencil className="w-3.5 h-3.5" /> Edit
        </Button>
      </div>

      {/* Company identity — read-only, sourced from Company Profile */}
      <div className="flex items-center gap-4 rounded-lg border border-border p-4">
        <div className="w-16 h-16 rounded-lg border-2 border-dashed border-outline flex items-center justify-center bg-surface-variant overflow-hidden shrink-0">
          {merged.logo_url ? (
            <img src={merged.logo_url} alt="Company logo" className="w-full h-full object-contain p-1" />
          ) : (
            <ImageIcon className="w-6 h-6 text-on-surface-variant/40" />
          )}
        </div>
        <div className="min-w-0">
          <p className="text-body-md font-medium text-on-surface truncate">{merged.company_name || 'Company name not set'}</p>
          <p className="text-body-sm text-on-surface-variant truncate">{merged.company_address || 'Address not set'}</p>
          <p className="text-body-sm text-on-surface-variant">
            {[merged.company_cin && `CIN: ${merged.company_cin}`, merged.company_gst && `GST: ${merged.company_gst}`].filter(Boolean).join(' · ') || 'CIN / GST not set'}
          </p>
        </div>
      </div>

      {/* Visual Settings */}
      <div className="space-y-4">
        <h4 className="font-medium text-on-surface flex items-center gap-2">
          <Palette className="w-4 h-4 text-primary" /> Visual
        </h4>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1">
            <label className="text-body-md font-medium text-on-surface">Accent Color</label>
            <div className="flex items-center gap-2">
              <input type="color" value={merged.accent_color ?? '#1a56db'} onChange={e => set('accent_color', e.target.value)}
                className="w-10 h-9 rounded cursor-pointer border border-border p-0.5" />
              <Input name="accent_color" value={merged.accent_color ?? '#1a56db'} onChange={e => set('accent_color', e.target.value)} className="flex-1 font-mono text-body-md" />
            </div>
          </div>
          <div className="space-y-1">
            <label className="text-body-md font-medium text-on-surface">Currency Symbol</label>
            <Input name="currency_symbol" value={merged.currency_symbol ?? '₹'} onChange={e => set('currency_symbol', e.target.value)} placeholder="₹" className="w-24" />
          </div>
        </div>
      </div>

      {/* Section Toggles */}
      <div className="space-y-3">
        <h4 className="font-medium text-on-surface">Show on Payslip</h4>
        <div className="space-y-3 rounded-lg border border-border p-4">
          <Toggle label="Bank Details (Account No., IFSC, Bank Name)" checked={merged.show_bank_details ?? true} onChange={v => set('show_bank_details', v)} />
          <Toggle label="Attendance (Working Days / Leave Days)" checked={merged.show_working_days ?? true} onChange={v => set('show_working_days', v)} />
          <Toggle label="Leave Summary" checked={merged.show_leave_summary ?? false} onChange={v => set('show_leave_summary', v)} />
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-3">
        <Button onClick={handleSave} loading={saveSettings.isPending} disabled={Object.keys(form).length === 0}>
          <Save className="w-4 h-4" /> Save Settings
        </Button>
        <Button variant="outline" onClick={() => setShowPreview(!showPreview)}>
          <Eye className="w-4 h-4" /> {showPreview ? 'Hide' : 'Preview'} Payslip
        </Button>
      </div>

      {/* Inline preview */}
      {showPreview && (
        <div className="rounded-lg border border-border overflow-hidden shadow-xs">
          <div className="bg-surface-variant px-4 py-2 text-body-sm font-medium text-on-surface-variant uppercase tracking-wide">
            Preview — Default Payslip Layout
          </div>
          <div className="p-6 bg-card text-black font-sans text-body-md">
            {/* Mini payslip preview */}
            <div className="flex items-start justify-between border-b-2 pb-4 mb-5" style={{ borderColor: merged.accent_color ?? '#1a56db' }}>
              <div className="flex items-center gap-3">
                {merged.logo_url && <img src={merged.logo_url} alt="logo" className="h-12 w-12 object-contain" />}
                <div>
                  <div className="text-base font-semibold" style={{ color: merged.accent_color ?? '#1a56db' }}>{merged.company_name || 'Your Company'}</div>
                  <div className="text-body-sm text-on-surface-variant">{merged.company_address || 'Company Address'}</div>
                  {merged.company_gst && <div className="text-body-sm text-on-surface-variant">GST: {merged.company_gst}</div>}
                </div>
              </div>
              <div className="text-right">
                <div className="font-semibold">Payslip</div>
                <div className="text-body-sm text-on-surface-variant">Month: July 2026</div>
                <div className="text-body-sm text-on-surface-variant">Date: 31 Jul 2026</div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 text-body-sm mb-4">
              <div><span className="font-semibold">Employee:</span> Sandesh Kajare</div>
              <div><span className="font-semibold">Designation:</span> Chief Operating Officer</div>
              {merged.show_bank_details !== false && <>
                <div><span className="font-semibold">Bank:</span> Induslnd Bank</div>
                <div><span className="font-semibold">A/C:</span> ****9329</div>
              </>}
              {merged.show_working_days !== false && <>
                <div><span className="font-semibold">Working Days:</span> 26</div>
                <div><span className="font-semibold">Leave Days:</span> 2</div>
              </>}
            </div>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <div className="font-semibold text-body-sm border-b mb-2 pb-1" style={{ borderColor: merged.accent_color ?? '#1a56db', color: merged.accent_color ?? '#1a56db' }}>Earnings</div>
                {[['Basic', 25000], ['HRA', 10000], ['Travelling Allowance', 3000]].map(([l, a]) =>
                  <div key={l as string} className="flex justify-between text-body-sm py-0.5"><span>{l}</span><span className="font-mono">{formatINR(Number(a))}</span></div>
                )}
              </div>
              <div>
                <div className="font-semibold text-body-sm border-b mb-2 pb-1 text-danger-text" style={{ borderColor: '#dc2626' }}>Deductions</div>
                {[['Provident Tax', 3000], ['Income Tax', 2500]].map(([l, a]) =>
                  <div key={l as string} className="flex justify-between text-body-sm py-0.5"><span>{l}</span><span className="font-mono">{formatINR(Number(a))}</span></div>
                )}
              </div>
            </div>
            <div className="flex justify-between font-semibold text-body-md p-2 rounded" style={{ backgroundColor: `${merged.accent_color ?? '#1a56db'}15` }}>
              <span>Net Salary</span>
              <span style={{ color: merged.accent_color ?? '#1a56db' }}>{merged.currency_symbol ?? '₹'}32,500</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
